/**
 * Media Carousel (E20-1193)
 *
 * An oversized media carousel for splash pages. Slides sit on a horizontal
 * scroll-snap track (mobile swipe + momentum come free from native scrolling).
 * Navigation is entirely user-driven — there is NO autoplay/auto-advance:
 *   - Desktop: prev/next ghost arrows, left/right arrow keys, and horizontal
 *     trackpad scroll each advance exactly one slide. Arrows disable at the ends
 *     (no wrap); horizontal scroll releases to the page once there's nothing left
 *     that way, and vertical scroll always passes through so the section can be
 *     scrolled past. A plain mouse wheel emits only deltaY, so it scrolls the page
 *     rather than the carousel — the arrows and arrow keys cover that case.
 *   - Mobile: horizontal swipe (arrows are hidden).
 * Only the active slide's video plays (muted, looping); the rest pause and show
 * their poster. prefers-reduced-motion suppresses playback entirely — every slide
 * stays on its poster — and scrolling is instant rather than smooth.
 * Listeners are cleaned up via AbortController.
 */
class MediaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');

    this.ticking = false; // rAF guard for scroll
    this.wheelLock = false; // one wheel gesture = one slide
    this.isDown = false; // pointer is held on the track
    this.dragged = false; // moved far enough to count as a drag

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.prevBtn?.addEventListener('click', () => this.go(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.go(1), { signal });
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.track.addEventListener('wheel', (e) => this.onWheel(e), { passive: false, signal });
    this.addEventListener('keydown', (e) => this.onKeydown(e), { signal });

    // Mouse / pen drag-to-scroll (touch uses native scrolling).
    this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
    this.track.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
    this.track.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
    this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e), { signal });

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener('change', () => this.update(), { signal });

    // Re-sync on resize: crossing the breakpoint swaps which video is visible, so
    // the active slide's newly-visible source needs to start and the hidden one stop.
    window.addEventListener('resize', () => this.update(), { passive: true, signal });

    this.update();
  }

  reduceMotion() {
    return this.motionQuery.matches;
  }

  // Snap line = the track's left content edge plus its scroll padding (the gutter).
  snapLine() {
    const padLeft = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    return this.track.getBoundingClientRect().left + padLeft;
  }

  // The slide whose left edge is nearest the snap line — correct at any slide
  // width / gap and mid-scroll.
  activeIndex() {
    const snap = this.snapLine();
    let nearest = 0;
    let min = Infinity;
    this.slides.forEach((slide, i) => {
      const dist = Math.abs(slide.getBoundingClientRect().left - snap);
      if (dist < min) {
        min = dist;
        nearest = i;
      }
    });
    return nearest;
  }

  scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const delta = slide.getBoundingClientRect().left - this.snapLine();
    this.track.scrollTo({
      left: this.track.scrollLeft + delta,
      behavior: this.reduceMotion() ? 'auto' : 'smooth',
    });
  }

  // Relative move, clamped at the ends (no wrap — arrows disable there).
  go(direction) {
    const target = Math.min(Math.max(this.activeIndex() + direction, 0), this.slides.length - 1);
    this.scrollToSlide(target);
  }

  onKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.go(1);
    }
  }

  // Wheel / trackpad advances one slide per gesture, but only on horizontal intent.
  // A predominantly vertical gesture is the page being scrolled, so it passes
  // straight through untouched. Taking the dominant axis instead meant a normal
  // downward trackpad scroll paged the carousel, and the only way out was to page
  // through every slide to reach an edge — the section became a scroll trap.
  onWheel(e) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

    const dir = e.deltaX > 0 ? 1 : -1;
    const active = this.activeIndex();
    const atEdge = (dir === -1 && active === 0) || (dir === 1 && active === this.slides.length - 1);
    if (atEdge) return;

    // Below the edge check on purpose: horizontal overscroll at either end still
    // releases to the page. The lock absorbs the rest of one flick's momentum, so
    // a gesture advances a single slide rather than one per event it emits.
    e.preventDefault();
    if (this.wheelLock) return;
    this.wheelLock = true;
    this.go(dir);
    setTimeout(() => {
      this.wheelLock = false;
    }, 600);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  onPointerDown(e) {
    this.dragged = false;
    // Touch uses native scrolling; only the primary (left) button drags.
    if (e.pointerType === 'touch' || e.button !== 0) return;
    this.isDown = true;
    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.startScroll = this.track.scrollLeft;
  }

  onPointerMove(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    // Button released outside the track (before capture) — end the drag.
    if (e.buttons === 0) {
      this.onPointerUp(e);
      return;
    }
    const dx = e.clientX - this.startX;
    if (!this.dragged) {
      if (Math.abs(dx) <= 5) return; // not a drag yet
      this.dragged = true;
      this.track.setPointerCapture(this.pointerId);
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = this.startScroll - dx;
  }

  onPointerUp(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    this.isDown = false;
    // Removing is-dragging re-enables scroll-snap, settling on the nearest slide.
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(e.pointerId)) {
      this.track.releasePointerCapture(e.pointerId);
    }
  }

  update() {
    const active = this.activeIndex();
    this.updateArrows(active);
    this.updateVideos(active);
  }

  updateArrows(active = this.activeIndex()) {
    if (this.prevBtn) this.prevBtn.disabled = active === 0;
    if (this.nextBtn) this.nextBtn.disabled = active === this.slides.length - 1;
  }

  // Play only the active slide's visible video; pause the rest so they hold on
  // their poster. Reduced motion keeps every slide on its poster.
  updateVideos(active = this.activeIndex()) {
    this.slides.forEach((slide, i) => {
      const isActive = i === active && !this.reduceMotion();
      slide.querySelectorAll('video').forEach((video) => {
        // Only the active slide's on-screen source plays; everything else — inactive
        // slides and the hidden breakpoint of the active slide — pauses on its poster.
        if (isActive && video.offsetParent !== null) {
          this.playVideo(video);
        } else {
          if (!video.paused) video.pause();
          // pause() alone freezes on the last frame; reload restores the poster
          // still so an inactive slide shows its poster, not a paused mid-frame.
          if (video.currentTime > 0) video.load();
        }
      });
    });
  }

  playVideo(video) {
    video.muted = true;
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        // Autoplay can be rejected before any interaction; the poster stays
        // visible, which is the intended fallback, so there's nothing to recover.
      });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('media-carousel')) {
  customElements.define('media-carousel', MediaCarousel);
}
